/**
 * True Singleton SSE Manager
 *
 * Ensures exactly ONE EventSource connection exists globally, regardless of:
 * - React component mount/unmount cycles
 * - React StrictMode double-mounting
 * - Navigation between pages
 *
 * Uses reference counting to auto-close when all subscribers disconnect.
 */

import { EventSource } from 'eventsource'
import { ensureFreshToken } from '../config/keycloakClient'
import { config } from '@/config'

export interface DocumentStatusEvent {
  document_id: string
  document_name: string
  event_type: string
  is_indexed: boolean
  metadata?: Record<string, any>
}

type EventCallback = (event: DocumentStatusEvent) => void
type ErrorCallback = (error: Event) => void
/**
 * Callback invoked when SSE is unavailable and subscribers should refresh data.
 * Called in two scenarios:
 * 1. When SSE connection fails after max retries (enters fallback mode)
 * 2. Periodically (every 30s) during fallback polling mode
 *
 * Subscribers should use this to refresh their data from the API.
 */
type DisconnectedCallback = () => void

class DocumentStatusStreamManager {
  private static instance: DocumentStatusStreamManager | null = null
  private eventSource: EventSource | null = null
  private callbacks: Set<EventCallback> = new Set()
  private errorCallbacks: Set<ErrorCallback> = new Set()
  private disconnectedCallbacks: Set<DisconnectedCallback> = new Set()
  private isConnecting: boolean = false
  private subscriberCount: number = 0
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 10
  private reconnectTimeout: NodeJS.Timeout | null = null
  private fallbackPollingInterval: NodeJS.Timeout | null = null
  private sseRetryTimeout: NodeJS.Timeout | null = null
  private isFallbackMode: boolean = false
  private hasConnectedBefore: boolean = false

  private constructor() {}

  static getInstance(): DocumentStatusStreamManager {
    if (!DocumentStatusStreamManager.instance) {
      DocumentStatusStreamManager.instance = new DocumentStatusStreamManager()
    }
    return DocumentStatusStreamManager.instance
  }

  /**
   * Subscribe to document status events.
   * @param onEvent - Called when a document status event is received
   * @param onError - Called when a connection error occurs
   * @param onDisconnected - Called when SSE is unavailable (after max retries or during fallback polling).
   *                         Use this to trigger data refresh from the API.
   * @returns Unsubscribe function
   */
  async subscribe(
    onEvent: EventCallback,
    onError?: ErrorCallback,
    onDisconnected?: DisconnectedCallback
  ): Promise<() => void> {
    this.subscriberCount++
    this.callbacks.add(onEvent)
    if (onError) {
      this.errorCallbacks.add(onError)
    }
    if (onDisconnected) {
      this.disconnectedCallbacks.add(onDisconnected)
    }

    if (!this.eventSource && !this.isFallbackMode) {
      await this.connect()
    }

    return () => {
      this.unsubscribe(onEvent, onError, onDisconnected)
    }
  }

  private unsubscribe(onEvent: EventCallback, onError?: ErrorCallback, onDisconnected?: DisconnectedCallback) {
    this.subscriberCount--
    this.callbacks.delete(onEvent)
    if (onError) {
      this.errorCallbacks.delete(onError)
    }
    if (onDisconnected) {
      this.disconnectedCallbacks.delete(onDisconnected)
    }

    if (this.subscriberCount === 0) {
      this.disconnect()
    }
  }

  private async connect() {
    if (this.isConnecting) {
      return
    }

    if (this.eventSource) {
      return
    }

    this.isConnecting = true

    try {
      // Always call ensureFreshToken to handle expired tokens, not just missing ones
      const token = await ensureFreshToken()
      
      if (!token) {
        this.isConnecting = false
        return
      }

      // Check if all subscribers left during the async auth call
      // to prevent creating an orphaned EventSource with no subscribers
      if (this.subscriberCount === 0) {
        this.isConnecting = false
        return
      }

      const eventSourceUrl = `${config.apiBaseUrl}/v1/documents/status-stream`

      const eventSource = new EventSource('', {
        fetch: async () => {
          return fetch(eventSourceUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })
        },
      })

      eventSource.onopen = () => {
        this.isConnecting = false

        // If this is a reconnect (not the first connection), notify subscribers
        // to refresh their data and catch any events missed during the disconnect.
        // PostgreSQL NOTIFY is fire-and-forget — events emitted while disconnected are lost.
        if (this.hasConnectedBefore) {
          console.log('SSE reconnected - notifying subscribers to refresh missed events')
          this.notifyDisconnected()
        }
        this.hasConnectedBefore = true

        this.reconnectAttempts = 0 // Reset only after successful connection

        // Stop fallback polling if we were in that mode
        this.stopFallbackPolling()

        // Clear any pending reconnect timeout to prevent it from closing this good connection
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout)
          this.reconnectTimeout = null
        }
      }

      eventSource.addEventListener('document_status', (event: MessageEvent) => {
        try {
          const data: DocumentStatusEvent = JSON.parse(event.data)
          this.callbacks.forEach(callback => {
            try {
              callback(data)
            } catch (err) {
              console.error('SSE callback error:', err)
            }
          })
        } catch (err) {
          console.error('SSE parse error:', err)
        }
      })

      eventSource.onerror = (error: Event) => {
        this.isConnecting = false
        console.warn('SSE connection error, will attempt reconnect')

        this.errorCallbacks.forEach(callback => {
          try {
            callback(error)
          } catch (err) {
            console.error('SSE error callback error:', err)
          }
        })

        // Close the eventSource immediately to prevent multiple onerror events
        // from inflating the reconnect counter
        if (this.eventSource) {
          try {
            this.eventSource.close()
          } catch (err) {
            // Ignore close errors
          }
          this.eventSource = null
        }

        // Auto-reconnect if we still have subscribers
        if (this.subscriberCount > 0) {
          this.scheduleReconnect()
        }
      }

      this.eventSource = eventSource
    } catch (err) {
      console.error('SSE connection failed:', err)
      this.isConnecting = false

      // Auto-reconnect on connection failure
      if (this.subscriberCount > 0) {
        this.scheduleReconnect()
      }
    }
  }

  private scheduleReconnect() {
    // Clear existing reconnect timeout first
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SSE max reconnect attempts reached, entering fallback polling mode')
      // Reset counter so future SSE retry gets fresh attempts
      this.reconnectAttempts = 0

      // Notify subscribers that SSE is disconnected
      this.notifyDisconnected()

      // Start fallback polling mode
      this.startFallbackPolling()
      return
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, ... up to 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
    this.reconnectAttempts++

    console.log(`SSE reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    this.reconnectTimeout = setTimeout(() => {
      // Check if all subscribers left during the delay
      if (this.subscriberCount === 0) {
        console.log('SSE reconnect cancelled - no subscribers remaining')
        return
      }

      // If another connect() is already in progress (e.g., from a new subscriber),
      // don't interfere - let that connection attempt complete
      if (this.isConnecting) {
        console.log('SSE reconnect skipped - connection already in progress')
        return
      }

      // Clean up old connection before reconnecting
      if (this.eventSource) {
        try {
          this.eventSource.close()
        } catch (err) {
          // Ignore close errors
        }
        this.eventSource = null
      }

      this.connect()
    }, delay)
  }

  private disconnect() {
    // Clear any pending reconnect
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    this.reconnectAttempts = 0

    // Stop fallback polling
    this.stopFallbackPolling()

    if (this.eventSource) {
      try {
        this.eventSource.close()
      } catch (err) {
        console.error('SSE disconnect error:', err)
      }
      this.eventSource = null
    }
  }

  /**
   * Notify all subscribers that SSE is disconnected (after max retries)
   * This allows them to start their own fallback behavior (e.g., polling API)
   */
  private notifyDisconnected() {
    this.disconnectedCallbacks.forEach(callback => {
      try {
        callback()
      } catch (err) {
        console.error('SSE disconnected callback error:', err)
      }
    })
  }

  /**
   * Start fallback polling mode when SSE connection fails
   * Notifies subscribers periodically (every 30s) and retries SSE after 60s
   */
  private startFallbackPolling() {
    if (this.isFallbackMode) {
      return // Already in fallback mode
    }

    this.isFallbackMode = true
    console.log('SSE fallback: starting polling mode')

    // Notify subscribers every 30s so they can refresh their data
    this.fallbackPollingInterval = setInterval(() => {
      console.log('SSE fallback: triggering poll notification')
      this.notifyDisconnected()
    }, 30000)

    // Try to reconnect SSE after 60s
    this.sseRetryTimeout = setTimeout(async () => {
      console.log('SSE fallback: attempting to reconnect SSE')
      this.stopFallbackPolling()

      if (this.subscriberCount > 0) {
        await this.connect()

        // If still not connected after reconnect attempt, restart fallback polling
        if (!this.eventSource && this.subscriberCount > 0) {
          this.startFallbackPolling()
        }
      }
    }, 60000)
  }

  /**
   * Stop fallback polling mode
   */
  private stopFallbackPolling() {
    if (this.fallbackPollingInterval) {
      clearInterval(this.fallbackPollingInterval)
      this.fallbackPollingInterval = null
    }

    if (this.sseRetryTimeout) {
      clearTimeout(this.sseRetryTimeout)
      this.sseRetryTimeout = null
    }

    this.isFallbackMode = false
  }

  isConnected(): boolean {
    return this.eventSource !== null
  }

  getSubscriberCount(): number {
    return this.subscriberCount
  }
}

export const sseManager = DocumentStatusStreamManager.getInstance()
